import { createSlice } from "@reduxjs/toolkit";
import { checkPrimaDopoCanShare } from "../actions";

const DAY_MS = 86400000;

function toYMD(v) {
  if (!v) return null;
  // already in YYYY-MM-DD
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;

  const d = v instanceof Date ? v : new Date(v);
  const ms = d.getTime();
  if (Number.isNaN(ms)) return null;

  // Use UTC parts to avoid timezone drift.
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const initialState = {
  loading: false,
  error: null,

  // selection (optional, kept for debugging / future reuse)
  startDate: null,
  endDate: null,
  background: null,
  phrase: "",

  includePhoto: false,
  includeWeight: false,
  includeMeasurements: {
    coscia: false,
    ombelicale: false,
    fianchi: false,
    petto: false,
    braccio: false,
  },

  // computed by backend
  canShare: null,
  previewImageUrl: null,
  canShareMeta: null, // raw payload from backend (if provided)

  // The dates the backend's answer actually refers to. They differ from the
  // requested ones when a picked date had no measurement and was snapped to the
  // nearest one on record.
  resolvedStartDate: null,
  resolvedEndDate: null,
  datesAdjusted: false,
  // Why can_share is false, when the backend can be specific about it.
  // "no_measurements" = there was nothing to snap to at all.
  reason: null,
};

const primaDopoSlice = createSlice({
  name: "primaDopo",
  initialState,
  reducers: {
    resetPrimaDopoState: () => ({ ...initialState }),

    // Used by the (page) implementation to initialize a sane default range
    // from backend metadata. This keeps the UI functional even if the backend
    // changes key names slightly (from/to vs start_date/end_date).
    setDefaultRangeFromInfo: (state, action) => {
      const info = action.payload;
      const def = info?.default_share || {};
      state.startDate = def.start_date || null;
      state.endDate = def.end_date || null;

      // Use backend-provided default can_share instead of forcing a can-share call on init.
      if (typeof def.can_share === "boolean") {
        state.canShare = def.can_share;
      }

      // Do not pre-select background/phrase/checkboxes here; the UI should only default dates.
      state.canShareMeta = null;
    },

    // Used by the create modal to keep a single object in sync
    setPrimaDopoSelection: (state, action) => {
      const sel = action?.payload || {};

      if (sel.startDate !== undefined) state.startDate = sel.startDate;
      if (sel.endDate !== undefined) state.endDate = sel.endDate;
      if (sel.background !== undefined) state.background = sel.background;
      if (sel.phrase !== undefined) state.phrase = sel.phrase;

      if (typeof sel.includePhoto === "boolean")
        state.includePhoto = sel.includePhoto;
      if (typeof sel.includeWeight === "boolean")
        state.includeWeight = sel.includeWeight;

      if (
        sel.includeMeasurements &&
        typeof sel.includeMeasurements === "object"
      ) {
        state.includeMeasurements = {
          ...state.includeMeasurements,
          ...sel.includeMeasurements,
        };
      }
    },

    // granular reducers (kept because existing code may use them)
    setStartDate: (state, action) => {
      state.startDate = action.payload;
    },
    setEndDate: (state, action) => {
      state.endDate = action.payload;
    },
    setBackground: (state, action) => {
      state.background = action.payload;
    },
    setPhrase: (state, action) => {
      state.phrase = action.payload;
    },
    toggleIncludePhoto: (state) => {
      state.includePhoto = !state.includePhoto;
    },
    toggleIncludeWeight: (state) => {
      state.includeWeight = !state.includeWeight;
    },
    toggleMeasurement: (state, action) => {
      const key = action.payload;
      if (!key) return;
      state.includeMeasurements[key] = !state.includeMeasurements[key];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkPrimaDopoCanShare.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkPrimaDopoCanShare.fulfilled, (state, action) => {
        state.loading = false;
        state.canShare = !!action.payload.can_share;
        state.previewImageUrl = action.payload.image;
        state.canShareMeta = action.payload.raw || null;
        state.resolvedStartDate = action.payload.resolvedStartDate || null;
        state.resolvedEndDate = action.payload.resolvedEndDate || null;
        state.datesAdjusted = !!action.payload.datesAdjusted;
        state.reason = action.payload.reason || null;
      })
      .addCase(checkPrimaDopoCanShare.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || "Error";
        state.canShare = null;
        state.previewImageUrl = null;
        state.canShareMeta = null;
        state.resolvedStartDate = null;
        state.resolvedEndDate = null;
        state.datesAdjusted = false;
        state.reason = null;
      });
  },
});

export const {
  resetPrimaDopoState,
  setDefaultRangeFromInfo,
  setPrimaDopoSelection,
  setStartDate,
  setEndDate,
  setBackground,
  setPhrase,
  toggleIncludePhoto,
  toggleIncludeWeight,
  toggleMeasurement,
} = primaDopoSlice.actions;

export default primaDopoSlice.reducer;
